import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { Grid, Typography, Box, IconButton } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";

import {
    BreadCrumbs,
    ExitConfirmationModal,
    LoadingBar,
    Tabs,
} from "../../components/Common";
import { trakrefFontFamily, trakrefFontFamilyBold, capitalizeFirstLetterInString } from "../../utils";
import UserInfo from "../../UserInfo";
import Routes from "../../routes";
import { TranslateW } from "../../Utils/Languages/Translate";
import { trakrefBlue } from "../../color_utils";

import EditProfiledetails from "./EditProfileDetails";
import EditProfileSettings from "./EditProfileSettings";

import "./Profile.css";
import useFormDirty from "../../hooks/useFormDirty";
import toast from "react-hot-toast";

const formatValue = (val) => {
    if (val && val.length > 0 && val[0] === "+") {
        const i = val.indexOf(" ");
        return val.substring(val.length + 1, i).trimStart();
    }
    return val;
};

const initialValue = {
    id: 0,
    name: "",
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    state: "",
    isReseller: true,
    country: "",
    requireAdminApproval: false,
    certificationType: "0",
    language: "en-US",
    userType: 1,
    userTypeName: "",
    hasOtherInstances: false,
    notificationTypeId: 1,
    weightUnits: "",
    userPermissions: [],
    company: "",
    address1: "",
    address2: "",
    county: "",
    title: "",
    city: "",
    zip: "",
    phone: "",
    cell: "",
    fax: "",
    headshot: "",
    shortName: "",
    description: "",
    preferredLeakDetectionMethodID: "",
    fkid: 0,
    fkName: "",
    countryCode: "+1",
    countryCode2: "+1"
};

const EditProfile = () => {
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorExist, setErrorExist] = useState(false);
    const [isExitModalOpen, setIsExitModalOpen] = useState(false);

    const [isCertificationNumberValid, setIsCertificationNumberValid] = useState(true);
    const [activeTab, setActiveTab] = useState(1);

    const [userInfo, setUserInfo] = useState(initialValue);
    const [uniqueFieldState, setUniqueFieldState] = useState({});

    const { initializeData, isDirty } = useFormDirty();

    const [options, setOptions] = useState({
        company: [],
        country: [],
        state: [],
        notificationType: [],
        certificationType: [],
        group: [],
        leakDetectionMethod: [],
        weightUnit: [
            { id: "Kgs", name: "Kgs" },
            { id: "Lbs", name: "Lbs" },
        ],
        language: [
            {
                id: "en-US",
                name: "English",
            },
            {
                id: "es-ES",
                name: "Spanish",
            },
            {
                id: "fr-FR",
                name: "French",
            },
        ],
    });

    const history = useHistory();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            const requestGetUserDetailsDropDown = fetch(
                Routes.User.GetUserDetailsDropDown()
            );
            const requestGetUserDetails = fetch(
                Routes.User.GetUserDetails(UserInfo.userId)
            );

            const [getUserDetailsDropDownResponse, getUserDetailsResponse] =
                await Promise.allSettled([
                    requestGetUserDetailsDropDown,
                    requestGetUserDetails,
                ]);

            const getUserDetailsDropDownResponseJSON =
                await getUserDetailsDropDownResponse.value.json();
            const getUserDetailsResponseJSON =
                await getUserDetailsResponse.value.json();

            if (getUserDetailsDropDownResponse.value.status === 200) {
                const {
                    servicers,
                    leakDetectionMethods,
                    notificationTypes,
                    certificationTypes,
                    groups,
                    countries,
                    states,
                } = getUserDetailsDropDownResponseJSON;
                const company = servicers.map(({ id, name }) => ({
                    id,
                    name,
                }));

                const leakDetectionMethod = leakDetectionMethods.map(
                    ({ id, name }) => ({
                        id,
                        name,
                    })
                );

                const notificationType = notificationTypes.map(({ id, name }) => ({
                    id,
                    name,
                }));

                const certificationType = certificationTypes.map(({ id, name }) => ({
                    id,
                    name,
                }));

                const group = groups.map(({ id, name }) => ({
                    id,
                    name,
                }));

                const country = countries.map(({ countryCode, countryName }) => ({
                    id: countryCode,
                    name: countryName,
                }));

                const state = states.map(({ stateCode, stateName }) => ({
                    id: stateCode,
                    name: stateName,
                }));

                setOptions((prev) => ({
                    ...prev,
                    company,
                    leakDetectionMethod,
                    notificationType,
                    certificationType,
                    group,
                    country,
                    state,
                }));
                setLoading(false);
            }

            if (getUserDetailsResponse.value.status === 200) {
                let countryCode = "+1";
                if (getUserDetailsResponseJSON.cell && getUserDetailsResponseJSON.cell[0] === "+") {
                    countryCode = getUserDetailsResponseJSON.cell.split(" ")[0].trimStart();
                }
                let countryCode2 = "+1";
                if (getUserDetailsResponseJSON.phone && getUserDetailsResponseJSON.phone[0] === "+") {
                    countryCode2 = getUserDetailsResponseJSON.phone.split(" ")[0].trimStart();
                }
                const newUserInfo = { ...userInfo, ...getUserDetailsResponseJSON, countryCode, countryCode2 };
                initializeData(newUserInfo);
                setUserInfo(newUserInfo);
            }
        };

        fetchData();
    }, []);

    const isFieldValid = (value) => {
        return !value || value.length <= 0;
    };

    const onhandleChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const onHandleValueChanged = (name, value) => {
        if (uniqueFieldState[name]) {
            setUniqueFieldState((prev) => ({ ...prev, [name]: undefined }));
        }

        let newUserInfo = {
            ...userInfo,
            [name]: value,
        };

        if (name === "firstName" || name === "lastName") {
            const fullName = capitalizeFirstLetterInString(`${newUserInfo.firstName.trim()} ${newUserInfo.lastName.trim()}`);
            const splitFullName = fullName.split(" ");
            const firstChar = splitFullName.length > 0 ?  splitFullName[0].charAt(0) : '';
            const secondChar = splitFullName.length > 1 ?  splitFullName[1].charAt(0) : '';
            newUserInfo = {
                ...newUserInfo,
                name: fullName,
                shortName: `${firstChar}${secondChar}`,
            };
        }

        setUserInfo(newUserInfo);
    };

    const onHandleProfileUpdate = async () => {
        try {
            setIsSaving(true);

            const { userName, firstName, lastName, email, state, country } = userInfo;

            const hasErrorInRequireFields =
                isFieldValid(userName) ||
                isFieldValid(firstName) ||
                isFieldValid(lastName) ||
                isFieldValid(email) ||
                isFieldValid(state) ||
                isFieldValid(country);

            if (hasErrorInRequireFields || errorExist) {
                setActiveTab(1);
                return;
            }

            const newUserInfo = {
                ...userInfo,
                cell: `${userInfo.countryCode} ${formatValue(userInfo.cell)}`,
                phone: `${userInfo.countryCode2} ${formatValue(userInfo.phone)}`,
            };

            const response = await fetch(
                Routes.User.UpdateUserDetails(UserInfo.userId),
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(newUserInfo),
                }
            );
            const data = await response.json();

            if (response.status === 200) {

                UserInfo.setFirstName(data.firstName);
                UserInfo.setLastName(data.lastName);
                UserInfo.setGroupId(data.groupId);
                UserInfo.setUserCulture(data.culture);
                UserInfo.setWeightUnits(data.weightUnits);

                toast.success("Your profile has been successfully updated.", { duration: 6000 });
                history.push("/Profile");
            } else if (response.status === 400) {
                const newUniqueFieldState = {};
                Object.keys(data).forEach((x) => {
                    newUniqueFieldState[x] = data[x][0];
                });
                setUniqueFieldState(newUniqueFieldState);
                setActiveTab(1);
            }
        } catch {
            setIsSaving(false);
            toast.error("Something went wrong!", { duration: 6000 });
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: "Your Profile", to: "/Profile" },
                    { text: "Edit" },
                ]}
            />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexDirection: "row",
                    maxWidth: "67%",
                }}
            >
                <Box
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        flexDirection: "row",
                    }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            if (isDirty(userInfo)) {
                                setIsExitModalOpen(true);
                                return;
                            }

                            history.push("/Profile");
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography
                        variant="h5"
                        sx={{ "&.MuiTypography-root": { fontFamily: trakrefFontFamily } }}
                        style={{ color: "#1A173B" }}
                    >
                        {TranslateW("Profile.EditProfileTitle")}
                    </Typography>
                </Box>

                <LoadingButton
                    variant="contained"
                    loading={isSaving}
                    onClick={onHandleProfileUpdate}
                    sx={{
                        "& .MuiCircularProgress-root": {
                            color: "#FFFFFF",
                            width: "21px !important",
                            height: "21px !important",
                        },
                        "&.MuiButton-root": {
                            fontFamily: trakrefFontFamilyBold,
                            backgroundColor: trakrefBlue,
                            letterSpacing: "1.2px",
                        },
                    }}
                >
                    <span style={{ color: isSaving ? "transparent" : "#fff" }}>
                        {TranslateW("Profile.SaveProfile")}
                    </span>
                </LoadingButton>
            </Box>

            <Grid container mb={4} ml={3} sx={{ maxWidth: "65%" }} spacing={2}>
                <Grid item xs={12}>
                    <Tabs
                        activeTab={activeTab}
                        onhandleChange={onhandleChange}
                        labels={[
                            TranslateW("Profile.UserDetails"),
                            TranslateW("Profile.Settings"),
                        ]}
                        tabElements={[
                            <EditProfiledetails
                                userInfo={userInfo}
                                onHandleValueChanged={onHandleValueChanged}
                                options={options}
                                isFieldValid={isFieldValid}
                                setErrorExist={setErrorExist}
                                uniqueFieldState={uniqueFieldState}
                            />,
                            <EditProfileSettings
                                userInfo={userInfo}
                                onHandleValueChanged={onHandleValueChanged}
                                options={options}
                                isFieldValid={isFieldValid}
                                isCertificationNumberValid={isCertificationNumberValid}
                            />,
                        ]}
                        tabPanelCustomStyle={{ marginTop: "30px" }}
                    />
                </Grid>
            </Grid>

            <ExitConfirmationModal open={isExitModalOpen}
                close={() => setIsExitModalOpen(false)}
                leaveOnClick={() => history.push("/Profile")}
                stayOnClick={() => setIsExitModalOpen(false)}
            />
        </>
    );
};

export default EditProfile;
